/* Copyright (c) 2020-present CloudIO Inc */

import { ActionItemType } from '../../registry/Base.type';
import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionCopyFilters';

const ActionCopyFiltersManager: ActionManagerFn<'ActionCopyFilters'> = (
  platform: Platform,
): ActionManager<'ActionCopyFilters'> => {
  return {
    ...baseActionManager(platform, 'ActionCopyFilters'),
    defaultValue,
    isChildAllowed: (page, item, childType) =>
      (['ActionCopyFilterAttribute'] as ActionItemType[]).includes(
        childType as ActionItemType,
      ),
    defaultChildren: ['ActionCopyFilterAttribute'],
    props: {
      ...baseActionProps,
      copyFrom: {
        label: 'Copy from Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        allowedTypes: ['ReadOnlyDataStore', 'DataStore', 'LocalDataStore'],
      },
      copyTo: {
        label: 'Copy to Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        allowedTypes: ['ReadOnlyDataStore', 'DataStore', 'LocalDataStore'],
      },
      copyAllMatchingFilters: {
        label: 'Copy all matching filters',
        type: 'boolean',
      },
    },
    isDataSourceAliasUsed: (item, alias) =>
      item.props.copyFrom === alias || item.props.copyTo === alias,
    name: 'Copy Filters',
    getDisplayText: (item) => `${item.props.copyFrom} to ${item.props.copyTo}}`,
    icon: { iconName: 'copy' },
    description: `**Overview**<br/>
The Copy Filters action is a high-level state management component designed to replicate filter criteria from one Data Store to another. It serves as a container and coordinator for filter synchronization, allowing the application to transfer search states or view constraints between different data entities.<br/><br/>
This component functions as a parent context for granular mapping definitions. While it establishes the source and destination Data Stores, it relies on its child component, ActionCopyFilterAttribute, to define explicit field-to-field mappings unless the automated matching feature is utilized. It is designed to work in conjunction with a subsequent Query action to effectuate the changes.

**Key Features**<br/>
* **Strict Child Hierarchy:** Enforces a specific structure by only permitting ActionCopyFilterAttribute components as children (isChildAllowed), ensuring that only valid mapping rules are nested within.
* **Dual Dependency Tracking:** Implements isDataSourceAliasUsed to simultaneously track dependencies for both the source (copyFrom) and destination (copyTo) aliases, ensuring accurate system-wide reference analysis.
* **Broad Store Compatibility:** Supports interaction with ReadOnlyDataStore, DataStore, and LocalDataStore for both source and target operations.
* **Automated Logic:** Includes a copyAllMatchingFilters property that automatically maps attributes with identical names without requiring manual child configurations.

**Properties**<br/>
* **Copy from Alias (Required):** A DataSourceAliasChooser property defining the source Data Store.
* **Copy to Alias (Required):** A DataSourceAliasChooser property defining the target Data Store.
* **Copy all matching filters:** A boolean property that enables automatic replication of filters between attributes sharing the same name in both stores.

**Use Cases**<br/>
* **Search Propagation:** Transferring user-selected filters from a "Search Form" (backed by a local or transient store) to a main "Results Grid" (backed by a persistent store).
* **View Synchronization:** Keeping two different data visualizations in sync by ensuring that a filter applied to one automatically updates the context of the other.`,
  };
};

export default ActionCopyFiltersManager;
